import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import { OrganizationMessageSeverity } from "../../../generated/graphql";
import { SEVERITY_DISPLAY } from "../message-content";
import { SEVERITY_ICON } from "./message-chips";

const styles = {
  caption: { color: "text.secondary", fontWeight: 600 },
  banner: {
    borderRadius: 1,
    py: 0,
    alignItems: "center",
    "& .MuiAlert-message": {
      display: "flex",
      alignItems: "center",
      gap: 1,
      minWidth: 0,
      overflow: "hidden",
    },
  },
  bannerText: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  bell: { p: 1.5, width: 320, maxWidth: "100%" },
  bellBody: { whiteSpace: "pre-wrap", wordBreak: "break-word" },
  empty: { color: "text.disabled", fontStyle: "italic" },
} satisfies Record<string, SxProps<Theme>>;

type MessagePreviewProps = {
  readonly severity: OrganizationMessageSeverity;
  readonly title: string;
  readonly body: string;
  readonly linkLabel: string | null;
};

/**
 * Roughly how customers see the message: the one-line banner for WARNING and
 * CRITICAL, and the bell row for every severity.
 */
const MessagePreview = ({
  severity,
  title,
  body,
  linkLabel,
}: MessagePreviewProps) => {
  const Icon = SEVERITY_ICON[severity];
  const display = SEVERITY_DISPLAY[severity];
  const hasTitle = title.trim().length > 0;
  const hasText = hasTitle || body.trim().length > 0;
  const showsBanner = severity !== OrganizationMessageSeverity.Info;

  return (
    <Stack spacing={2}>
      {showsBanner && (
        <Box>
          <Typography variant="caption" sx={styles.caption}>
            BANNER
          </Typography>
          <Alert
            severity={display.color}
            sx={styles.banner}
            action={
              <Stack direction="row" alignItems="center">
                {linkLabel !== null && linkLabel.length > 0 && (
                  <Button size="small" color="inherit">
                    {linkLabel}
                  </Button>
                )}
                <IconButton size="small" color="inherit" aria-label="Close">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
            }
          >
            {hasTitle && (
              <Typography noWrap variant="body2" fontWeight={600}>
                {title}
              </Typography>
            )}
            <Typography variant="body2" sx={styles.bannerText}>
              {hasText ? body : "Your message"}
            </Typography>
          </Alert>
        </Box>
      )}
      <Box>
        <Typography variant="caption" sx={styles.caption}>
          BELL
        </Typography>
        <Paper variant="outlined" sx={styles.bell}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Icon fontSize="small" color={display.color} />
            <Typography variant="caption" color="primary" fontWeight={600}>
              Tenet
            </Typography>
            <Typography
              variant="caption"
              color={`${display.color}.main`}
              fontWeight={600}
            >
              {display.label}
            </Typography>
          </Stack>
          {hasText ? (
            <>
              <Typography variant="body2" fontWeight={600}>
                {hasTitle ? title : body}
              </Typography>
              {hasTitle && body.length > 0 && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={styles.bellBody}
                >
                  {body}
                </Typography>
              )}
            </>
          ) : (
            <Typography variant="body2" sx={styles.empty}>
              Your message
            </Typography>
          )}
        </Paper>
      </Box>
    </Stack>
  );
};

export default MessagePreview;
